import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { Footer } from './Footer';
import { Header } from './Header';

/**
 * Reveals `.reveal` blocks as they scroll into view. Blocks already on screen are marked
 * visible in the same task that enables the hidden state, so nothing flashes; without JS
 * (or before hydration) every block is simply visible.
 */
function useScrollReveal() {
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px' },
    );
    const scan = () => {
      const vh = window.innerHeight;
      document.querySelectorAll<HTMLElement>('.reveal:not(.is-visible):not([data-observed])').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < vh && r.bottom > 0) el.classList.add('is-visible');
        else {
          el.dataset.observed = '';
          io.observe(el);
        }
      });
    };
    scan();
    document.documentElement.classList.add('js');
    // Pick up blocks from routes that mount later (lazy chunks, client-side navigation).
    const mo = new MutationObserver(scan);
    mo.observe(document.getElementById('main')!, { childList: true, subtree: true });
    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);
}

export function Layout({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const first = useRef(true);

  // On client-side navigation: jump to the #hash target if any, otherwise to the top.
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const hash = window.location.hash.slice(1);
    const target = hash ? document.getElementById(hash) : null;
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [location]);

  useScrollReveal();

  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:bg-primary focus:text-gold-light focus:px-4 focus:py-2 focus:rounded"
      >
        Skip to content
      </a>
      <Header />
      <main id="main" className="w-full pt-20 bg-surface">
        {children}
      </main>
      <Footer />
    </>
  );
}
